import { useMemo } from 'react';
import { isEmpty } from 'lodash';

import {
  NUMBER_OF_SWATCHES_TO_SHOW,
  DEFAULT_COLOR_FILTERS,
  DEFAULT_PRODUCTS_OBTAINED,
  DEFAULT_UPHOLSTERY_LABEL_FILTERS,
} from 'commons/constants';
import useAllProductClearanceStock from 'data-hooks/useAllProductClearanceStock';
import getLowestTierVariantFirst from './helpers/getLowestTierVariantFirst';

// This hook generates the variantsToShow property for every product in the
// products array, and returns a copy of the products array that includes this
// additional property for each product. If specified, the colorFilters array
// is used to filter product variants to only include those that match the
// specified colors.

// The colorFilters array is of the format:  ['green', 'red']
const useProductVariantsToShow = ({
  colorFilters = DEFAULT_COLOR_FILTERS,
  products = DEFAULT_PRODUCTS_OBTAINED,
  upholsteryLabelFilters = DEFAULT_UPHOLSTERY_LABEL_FILTERS,
}) => {
  const { data: clearanceData } = useAllProductClearanceStock();

  const productsWithVariantsToShow = useMemo(
    () =>
      products.map((product, index) => {
        const { variants = [], variantsTotal = 0 } = product;

        // Only show variants that are valid for the selected color filters
        const matchingVariantsForColors = colorFilters?.length
          ? variants.filter(({ color: variantColor }) =>
              colorFilters.some(color => color.includes(variantColor))
            )
          : variants;

        // Only show variants that are valid for the selected upholstery labels
        const matchingVariants = upholsteryLabelFilters?.length
          ? matchingVariantsForColors.filter(({ label: variantLabel }) =>
              upholsteryLabelFilters.some(
                upholsteryLabel =>
                  !!upholsteryLabel && variantLabel.includes(upholsteryLabel)
              )
            )
          : matchingVariantsForColors;

        // If the first variant is not a tier 1 variant, move the first tier 1
        // variant found to the start of array. If no tier 1 variant is found,
        // then move the first tier 2 variant instead, and so on.
        const variantsWithLowestTierFirst = getLowestTierVariantFirst(
          matchingVariants,
          product.id
        );

        // Show maximum of 6 swatches
        const variantsToShow =
          variantsWithLowestTierFirst?.length > 0
            ? variantsWithLowestTierFirst.slice(0, NUMBER_OF_SWATCHES_TO_SHOW)
            : variants.slice(0, NUMBER_OF_SWATCHES_TO_SHOW);

        const productClearanceData = clearanceData?.[product.id];
        const isClearance = !isEmpty(productClearanceData);

        return {
          ...product,
          isClearance,
          moreVariantsCount: variantsToShow.length
            ? variantsTotal - variantsToShow.length
            : 0,
          position: index + 1,
          variantsToShow,
        };
      }),
    [colorFilters, products, upholsteryLabelFilters, clearanceData]
  );

  return productsWithVariantsToShow;
};

export default useProductVariantsToShow;
